import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL, ENDPOINTS } from '../../constants/api';
import { decodeToken, getToken } from '../../utils/TokenUtils';
import { useParams } from 'react-router-dom';

export default function ApiDocumentation() {
  const { link_id } = useParams();
  const [apiData, setApiData] = useState(null);
  const [error, setError] = useState(null);
  const token = getToken();
  const companyName = decodeToken(token, 'companyName');
  const logo = decodeToken(token, 'logo');

  const [includeLogin, setIncludeLogin] = useState(false);

  useEffect(() => {
    const res = window.confirm(
      'Do you want to include login information in this API documentation?'
    );
    setIncludeLogin(res);
  }, []);

  const generatedDate = new Date().toLocaleString();
  const generatedUser = decodeToken(token, 'name');

  useEffect(() => {
    if (!link_id) return;

    axios
      .get(`${API_URL}/${ENDPOINTS.SYS}/api-documentation/${link_id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => setApiData(res.data))
      .catch((err) => {
        console.error(err);
        setError('Failed to load API data.');
      });
  }, [link_id]);

  const getMethod = (form_type) =>
    ['report', 'invoice-route'].includes(form_type) ? 'GET' : 'POST';

  const getResponseExamples = (formType) => {
    if (formType.includes('modal') || formType.includes('form')) {
      return {
        success: {
          message: 'Form submitted successfully',
          success: 1,
          status: true,
        },
        duplicate: {
          message: 'Duplicate entry found',
          success: 0,
          status: false,
        },
        error: {
          message: 'An unexpected error occurred',
          success: 0,
          status: false,
        },
      };
    } else if (formType === 'report') {
      return {
        success: {
          success: 10,
          status: true,
          result: [{ id: 1, name: 'Sample report item' }],
        },
        error: {
          success: 0,
          status: false,
          message: 'Failed to retrieve report data',
        },
      };
    } else {
      return {
        error: {
          message: 'Unknown response type',
        },
      };
    }
  };

  if (error) return <p style={{ color: 'red' }}>{error}</p>;
  if (!apiData) return <p>Loading documentation...</p>;

  const { link, inputs } = apiData;
  const endpoint = link[0];
  const method = getMethod(endpoint.form_type);
  const responses = getResponseExamples(endpoint.form_type);

  return (
    <div
      style={{
        fontFamily: 'Arial, sans-serif',
        maxWidth: '900px',
        margin: 'auto',
        padding: 20,
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center', // ✅ THIS FIXES IT
          marginBottom: 30,
        }}
      >
        <img
          src={`${API_URL}/${logo}`}
          alt="Company Logo"
          style={{ width: 60, marginRight: 20 }}
        />
        <h1>{companyName}</h1>
      </header>
      {/* Title */}
      <h2 style={{ textAlign: 'center', marginBottom: 20 }}>
        {endpoint.name} API Documentation
      </h2>

      {/* Meta Info */}
      <p style={{ textAlign: 'center', color: '#666', marginBottom: 30 }}>
        Generated on: <strong>{generatedDate}</strong> <br />
        Generated by: <strong>{generatedUser}</strong>
      </p>

      {/* Endpoint Info */}
      <section>
        <h3>{endpoint.name}</h3>
        <p>
          <strong>Endpoint:</strong>{' '}
          <code>
            {API_URL}/api/sys/create/{endpoint.route}
          </code>
        </p>
        <p>
          <strong>Method:</strong> {method}
        </p>
        <p>
          <strong>Authorization:</strong> Bearer Token (JWT)
        </p>
      </section>

      {/* Parameters */}
      <section>
        <h3>Parameters</h3>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={tableHeaderStyle}>Name</th>
              <th style={tableHeaderStyle}>Data Type</th>
              <th style={tableHeaderStyle}>Required</th>
            </tr>
          </thead>
          <tbody>
            {inputs.map((input, idx) => (
              <tr key={idx}>
                <td style={tableCellStyle}>{input.name}</td>
                <td style={tableCellStyle}>
                  {input.datatype
                    .replace('varchar', 'string')
                    .replace('text', 'string') || 'N/A'}
                </td>
                <td style={tableCellStyle}>
                  {input.is_required === 'required' ? 'Yes' : 'No'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* Sample Request */}
      <section>
        <h3>API Request</h3>
        <pre style={codeBlockStyle}>
          {`${method} ${API_URL}/api/sys/create/${endpoint.route}

Headers:
  Authorization: Bearer YOUR_JWT_TOKEN
  Content-Type: application/json

Body:
{
${inputs
  .filter((i) => i.is_required === 'required')
  .map(
    (i) =>
      `  "${i.name}": "<${
        i.datatype.replace('varchar', 'string').replace('text', 'string') ||
        'value'
      }>"`
  )
  .join(',\n')}
}
`}
        </pre>
      </section>

      {/* Responses */}
      <section>
        <h3>API Responses</h3>
        {Object.entries(responses).map(([key, response]) => (
          <div key={key}>
            <strong>{key.charAt(0).toUpperCase() + key.slice(1)}:</strong>
            <pre style={codeBlockStyle}>
              {JSON.stringify(response, null, 2)}
            </pre>
          </div>
        ))}
      </section>

      {/* Optional Login Section */}
      {includeLogin && (
        <section>
          <h3>User Login API</h3>
          <p>
            <strong>Endpoint:</strong>{' '}
            <code>{API_URL}/api/users/login/user-login</code>
          </p>
          <p>
            <strong>Method:</strong> POST
          </p>

          <pre style={codeBlockStyle}>
            {`POST ${API_URL}/api/users/login/user-login

Body:
{
  "email/username": "<email or username>",
  "password": "<password>"
}
`}
          </pre>

          <p style={{ color: '#444', fontSize: 14 }}>
            User credentials (username/email and password) will be sent via
            WhatsApp or Email .
          </p>
        </section>
      )}

      {/* Footer */}
      <footer
        style={{
          marginTop: 50,
          textAlign: 'center',
          fontSize: '0.9em',
          color: '#777',
        }}
      >
        <p>
          &copy; {new Date().getFullYear()} {companyName} — All Rights Reserved
        </p>
        <p style={{ marginTop: 5 }}>
          Powered by <strong>Kaafiye Technology</strong>
        </p>
      </footer>
    </div>
  );
}

/* Styles */
const tableStyle = {
  borderCollapse: 'collapse',
  width: '100%',
  marginBottom: '20px',
};

const tableHeaderStyle = {
  border: '1px solid #ddd',
  padding: '10px',
  backgroundColor: '#f5f5f5',
  textAlign: 'left',
};

const tableCellStyle = {
  border: '1px solid #ddd',
  padding: '10px',
};

const codeBlockStyle = {
  background: '#111',
  color: '#0f0',
  padding: '15px',
  borderRadius: '6px',
  overflowX: 'auto',
};
